import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  TextField,
  IconButton,
  Badge,
  Divider,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  Add as AddIcon,
  EmojiEmotions as EmojiIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import Popover from '@mui/material/Popover';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';

export default function Chat() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openNewChat, setOpenNewChat] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Common emojis
  const commonEmojis = [
    '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂',
    '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋',
    '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳',
    '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😣', '😖', '😫',
    '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳',
    '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭',
    '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
    '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢',
    '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹',
    '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃',
    '👍', '👎', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘',
    '👌', '🤏', '👈', '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐',
    '✋', '🖖', '👏', '🙌', '🤲', '🤝', '🙏', '✍️', '💅', '🤳',
    '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🦷',
    '🦴', '👀', '👁', '👅', '👄', '💋', '💘', '💝', '💖', '💗',
    '💓', '💞', '💕', '💟', '❣️', '💔', '❤️', '🧡', '💛', '💚',
    '💙', '💜', '🖤', '🤍', '🤎', '💯', '💢', '💥', '💫', '💦',
    '💨', '🕳️', '💣', '💬', '👤', '👥', '🗣️', '👶', '🧒', '👦',
    '👧', '🧑', '👱', '👨', '🧔', '👩', '🧓', '👴', '👵', '🙍',
    '🙎', '🙅', '🙆', '💁', '🙋', '🧏', '🙇', '🤦', '🤷', '👮',
    '🕵️', '💂', '🥷', '👷', '🤴', '👸', '👳', '👲', '🧕', '🤵',
    '👰', '🤰', '🤱', '👼', '🎅', '🤶', '🦸', '🦹', '🧙', '🧚',
    '🧛', '🧜', '🧝', '🧞', '🧟', '💆', '💇', '🚶', '🧍', '🧎',
    '🏃', '💃', '🕺', '🕴️', '👯', '🧘', '🧗', '🤺', '🏇', '⛷️',
    '🏂', '🏌️', '🏄', '🚣', '🏊', '⛹️', '🏋️', '🚴', '🚵', '🤸',
    '🤼', '🤽', '🤾', '🤹', '🧗', '🛀', '🛌', '👭', '👫', '👬',
    '💏', '💑', '👪', '👨‍👩‍👧', '👨‍👩‍👧‍👦', '👨‍👩‍👦‍👦', '👨‍👩‍👧‍👧', '👩‍👩‍👦', '👩‍👩‍👧', '👩‍👩‍👧‍👦',
    '👩‍👩‍👦‍👦', '👩‍👩‍👧‍👧', '👨‍👨‍👦', '👨‍👨‍👧', '👨‍👨‍👧‍👦', '👨‍👨‍👦‍👦', '👨‍👨‍👧‍👧', '👩‍👦', '👩‍👧', '👩‍👧‍👦',
    '👩‍👦‍👦', '👩‍👧‍👧', '👨‍👦', '👨‍👧', '👨‍👧‍👦', '👨‍👦‍👦', '👨‍👧‍👧', '👋', '🤚', '🖐',
    '✋', '🖖', '👏', '🙌', '🤲', '🤝', '🙏', '✍️', '💅', '🤳',
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE_URL}/api/chat/conversations`, { headers });
      const data = await res.json();
      if (res.ok) {
        setConversations(data.conversations || []);
      }
    } catch (e) {
      console.error('Failed to load conversations:', e);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE_URL}/api/chat/unread-count`, { headers });
      const data = await res.json();
      if (res.ok) {
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) {
      console.error('Failed to load unread count:', e);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE_URL}/api/users`, { headers });
      const data = await res.json();
      if (res.ok) {
        // Filter out current user
        const filtered = (data.users || []).filter(u => u.id !== user?.id);
        setUsers(filtered);
      }
    } catch (e) {
      console.error('Failed to load users:', e);
    }
  };

  const loadMessages = async (conversationId) => {
    if (!conversationId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}/messages`, { headers });
      const data = await res.json();
      if (res.ok) {
        setMessages(data.messages || []);
        // Mark messages as seen
        await fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}/seen`, {
          method: 'POST',
          headers,
        });
        loadUnreadCount();
        loadConversations();
      }
    } catch (e) {
      console.error('Failed to load messages:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
    loadUnreadCount();
    loadUsers();

    // Poll for new messages every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      if (selectedConversation) {
        loadMessages(selectedConversation.id);
      }
      loadConversations();
      loadUnreadCount();
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedConversation]);

  const handleConversationClick = (conv) => {
    setSelectedConversation(conv);
    loadMessages(conv.id);
  };

  const handleEmojiClick = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage((prev) => prev + emoji);
    handleEmojiClose();
  };

  const handleLikeDislike = async (messageId, reactionType) => {
    if (!selectedConversation) return;

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const res = await fetch(`${API_BASE_URL}/api/chat/messages/${messageId}/like`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reactionType }),
      });

      const data = await res.json();
      if (res.ok) {
        // Reload messages to get updated counts
        loadMessages(selectedConversation.id);
      } else {
        console.error('Failed to update reaction:', data.error);
      }
    } catch (e) {
      console.error('Failed to update reaction:', e);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const res = await fetch(`${API_BASE_URL}/api/chat/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipientId: selectedConversation.other_user_id,
          body: newMessage.trim(),
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setNewMessage('');
        loadMessages(selectedConversation.id);
        loadConversations();
        loadUnreadCount();
      } else {
        alert(data.error || 'Failed to send message');
      }
    } catch (e) {
      console.error('Failed to send message:', e);
      alert('Failed to send message');
    }
  };

  const handleNewChat = async () => {
    if (!selectedUserId) return;

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Send a dummy message to create conversation
      const res = await fetch(`${API_BASE_URL}/api/chat/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipientId: Number(selectedUserId),
          body: '👋',
        }),
      });

      if (res.ok) {
        setOpenNewChat(false);
        setSelectedUserId('');
        loadConversations();
        // Find and select the new conversation
        setTimeout(() => {
          loadConversations();
        }, 500);
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to start conversation');
      }
    } catch (e) {
      console.error('Failed to start conversation:', e);
      alert('Failed to start conversation');
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      (conv.other_user_name || '').toLowerCase().includes(search) ||
      (conv.other_user_email || '').toLowerCase().includes(search) ||
      (conv.last_message || '').toLowerCase().includes(search)
    );
  });

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-IN', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }
  };

  const getInitials = (name, email) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <Layout title="Chat">
      <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', gap: 2 }}>
        {/* Conversations List */}
        <Paper
          sx={{
            width: 350,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Messages
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpenNewChat(true)}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <AddIcon />
              </IconButton>
            </Stack>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mt: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <List sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
            {filteredConversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No conversations yet</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenNewChat(true)}
                  sx={{ mt: 2 }}
                >
                  Start New Chat
                </Button>
              </Box>
            ) : (
              filteredConversations.map((conv) => (
                <ListItem
                  key={conv.id}
                  button
                  selected={selectedConversation?.id === conv.id}
                  onClick={() => handleConversationClick(conv)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      '&:hover': {
                        bgcolor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={conv.unread_count > 0 ? conv.unread_count : null}
                      color="error"
                      overlap="circular"
                    >
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getInitials(conv.other_user_name, conv.other_user_email)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: conv.unread_count > 0 ? 600 : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {conv.other_user_name || conv.other_user_email || 'Unknown User'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: conv.unread_count > 0 ? 500 : 400,
                          }}
                        >
                          {conv.last_message || 'No messages yet'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(conv.last_message_time)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>
        </Paper>

        {/* Chat Window */}
        <Paper
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {selectedConversation ? (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {getInitials(
                      selectedConversation.other_user_name,
                      selectedConversation.other_user_email
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {selectedConversation.other_user_name ||
                        selectedConversation.other_user_email ||
                        'Unknown User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedConversation.other_user_email}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, bgcolor: 'background.default' }}>
                {loading ? (
                  <Typography>Loading messages...</Typography>
                ) : messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography color="text.secondary">No messages yet. Start the conversation!</Typography>
                  </Box>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.sender_id === user?.id;
                    const isSeen = isOwn ? msg.recipient_seen_at : msg.seen_at;

                    return (
                      <Box
                        key={msg.id}
                        sx={{
                          display: 'flex',
                          justifyContent: isOwn ? 'flex-end' : 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '70%',
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: isOwn ? 'primary.main' : 'background.paper',
                            color: isOwn ? 'white' : 'text.primary',
                            boxShadow: 1,
                          }}
                        >
                          <Typography variant="body1">{msg.body}</Typography>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{ opacity: 0.7, fontSize: '0.7rem' }}
                            >
                              {formatTime(msg.created_at)}
                            </Typography>
                            {isOwn && (
                              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                                {isSeen ? '✓✓' : '✓'}
                              </Typography>
                            )}
                          </Stack>
                          {/* Like/Dislike Buttons */}
                          <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleLikeDislike(msg.id, 'like')}
                              sx={{
                                color: msg.user_reaction === 'like' ? 'success.main' : (isOwn ? 'rgba(255,255,255,0.7)' : 'text.secondary'),
                                fontSize: '0.875rem',
                                padding: '4px',
                                '&:hover': {
                                  bgcolor: isOwn ? 'rgba(255,255,255,0.1)' : 'action.hover',
                                },
                              }}
                            >
                              <ThumbUpIcon fontSize="small" />
                              {(msg.like_count || 0) > 0 && (
                                <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                  {msg.like_count}
                                </Typography>
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleLikeDislike(msg.id, 'dislike')}
                              sx={{
                                color: msg.user_reaction === 'dislike' ? 'error.main' : (isOwn ? 'rgba(255,255,255,0.7)' : 'text.secondary'),
                                fontSize: '0.875rem',
                                padding: '4px',
                                '&:hover': {
                                  bgcolor: isOwn ? 'rgba(255,255,255,0.1)' : 'action.hover',
                                },
                              }}
                            >
                              <ThumbDownIcon fontSize="small" />
                              {(msg.dislike_count || 0) > 0 && (
                                <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                  {msg.dislike_count}
                                </Typography>
                              )}
                            </IconButton>
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={handleEmojiClick}
                    sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <EmojiIcon />
                  </IconButton>
                  <TextField
                    fullWidth
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={4}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                  >
                    <SendIcon />
                  </IconButton>
                </Stack>
                
                {/* Emoji Picker Popover */}
                <Popover
                  open={Boolean(emojiAnchorEl)}
                  anchorEl={emojiAnchorEl}
                  onClose={handleEmojiClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      maxWidth: 320,
                      maxHeight: 300,
                      overflow: 'auto',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(8, 1fr)',
                      gap: 0.5,
                    }}
                  >
                    {commonEmojis.map((emoji, index) => (
                      <IconButton
                        key={index}
                        onClick={() => handleEmojiSelect(emoji)}
                        sx={{
                          fontSize: '1.5rem',
                          width: 36,
                          height: 36,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {emoji}
                      </IconButton>
                    ))}
                  </Box>
                </Popover>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a conversation to start chatting
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenNewChat(true)}
              >
                Start New Chat
              </Button>
            </Box>
          )}
        </Paper>

        {/* New Chat Dialog */}
        <Dialog open={openNewChat} onClose={() => setOpenNewChat(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Start New Chat</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              select
              label="Select User"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              sx={{ mt: 1 }}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.full_name || u.email} {u.email && `(${u.email})`}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewChat(false)}>Cancel</Button>
            <Button
              onClick={handleNewChat}
              variant="contained"
              disabled={!selectedUserId}
            >
              Start Chat
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
